import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { TenderType } from 'src/common/enumerations/tender-type';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import {
  TenderExchangeAmountIsOverOrUnderLimitsException,
  TenderExchangeAmountNotAccordingMultiplesException,
} from 'src/common/helpers/error-code';
import { IButtonParameter } from 'src/common/interfaces/dtos/ICommandsGroup';
import { PaymentUsage } from 'src/common/interfaces/dtos/IPreTenderAdd';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { ITenderExchange } from 'src/common/interfaces/dtos/ITenderExchange';
import { ITenderExchangeDetails } from 'src/common/interfaces/states/ITenderExchangeState';
import { TenderExchangeActions } from 'src/common/redux/reducers/tender-exchange-reducer';
import { EpsData } from 'src/devices/payment/dtos/eps-data';
import { hasCardData, waitForCardDetails } from 'src/devices/payment/get-card-details';
import {
  beginPaymentSession,
  cancelPinpadOperation,
  enableCardEntry,
  endPaymentSession,
  inPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import { PmiStatusCodes } from 'src/devices/payment/pmi-status-codes-enums';
import { cancelTenderPayment } from 'src/home-page/business-logic/cancel-tender-payment';
import { getTicketTotal } from 'src/home-page/business-logic/get-ticket-total';
import { voidEmptyTransaction } from 'src/home-page/business-logic/void-transaction';
import { initiateTransaction } from 'src/items-list/business-logic/initiate-transaction';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import {
  ALPHA_NUMERIC_VOUCHER_MODAL,
  showSearchTenderVoucherModal,
} from 'src/tender-voucher/tender-exchange-payment-voucher';
import { voucherPayment } from 'src/tendering/business-logic/barcode-tender-add';
import {
  addEFTTender,
  showTenderingAndGetCardDetails,
} from 'src/tendering/business-logic/handle-eft-tendering';
import { addTender, AddTenderProps } from 'src/tendering/business-logic/handle-tender-payment';
import { closeEftTenderingView } from 'src/tendering/components/process-tender/tender-processing-view';
import { pinPadDisconnectModal } from '../../common/components/pinpad-disconnected-modal';
import { EJRecordTypes } from '../../common/enumerations/ej-record-types';
import { store } from '../../common/redux/configure-store';
import {
  closeTenderExchangePage,
  ITenderExchangeViewProps,
  showTenderExchangePage,
} from '../component/tender-exchange-view';

const TenderExchange = 'TenderExchange';

export const handleTenderExchange = async (parameters?: IButtonParameter[]): Promise<void> => {
  const { tenderExchanges } = store.getState().tenderExchange;
  let tenderExchangeId = parameters?.find(p => p.name === TenderExchange)?.value;

  if (!tenderExchangeId) {
    tenderExchangeId = store.getState().tenderExchange.tenderExchangeDetails.tenderExchangeId;
  } else {
    store.dispatch(TenderExchangeActions.updateTenderExchangeDetails({ tenderExchangeId }));
  }

  const tenderExchange = tenderExchanges.find(exchange => exchange.id === tenderExchangeId);

  if (!tenderExchange) {
    const errorModal = new EmeraldErrorModal({
      message: 'TenderExChangeNotFound',
      subMessage: 'TenderExchangeNotFound_subText',
      subMessageArgs: { arg: tenderExchangeId || 'undefined' },
    });
    popupMessage(errorModal);
  } else {
    let showInvalidTenderModal = false;
    const validTenderOptions = (tenders: ITender[]) => {
      const rawTenders = store.getState().tenderMenu.sellableTenders.rawTenders;
      tenders.forEach((tender, index) => {
        if (!tender.type) {
          const tenderWithDetails = rawTenders.find(i => i.id === tender.id);
          if (tenderWithDetails) {
            tenders[index] = tenderWithDetails ?? tender;
          } else {
            showInvalidTenderModal = true;
          }
        }
      });
      return tenders;
    };
    tenderExchange.purchaseTenders = validTenderOptions(tenderExchange.purchaseTenders);
    tenderExchange.refundTenders = validTenderOptions(tenderExchange.refundTenders);

    if (store.getState().transaction.transactionIdExists) {
      gotoTenderExchangePage(tenderExchange);
    } else {
      return withSpinner(() =>
        initiateTransaction({ tenderExchangeId: tenderExchange.id }).then(getTicketTotal),
      )
        .then(() => {
          gotoTenderExchangePage(tenderExchange);
          if (showInvalidTenderModal) {
            const errorModal = new EmeraldErrorModal({
              type: EmeraldDialogTypes.Info,
              message: 'InvalidTender',
              subMessage: 'InvalidTenderConfigured',
            });
            popupMessage(errorModal);
          }
        })
        .catch(popupMessage);
    }
  }
};

export const gotoTenderExchangePage = (tenderExchange: ITenderExchange): void => {
  const cancelTenderAction = async (): Promise<void> => {
    const { ejItems } = store.getState().itemList;
    const paymentTender = ejItems.find(item => item.Type === EJRecordTypes.Tender);
    const refundTender = ejItems.find(item => item.Type === EJRecordTypes.Refund);

    if (inPaymentSession()) {
      await cancelPinpadOperation();
      await endPaymentSession();
    }
    if (paymentTender) {
      await cancelTenderPayment(paymentTender.SequenceNumber);
    }
    if (refundTender) {
      await cancelTenderPayment(refundTender.SequenceNumber, true);
    }
  };

  const onCancel = async (): Promise<void> => {
    return withSpinner(() =>
      cancelTenderAction()
        .then(() => voidEmptyTransaction())
        .then(closeTenderExchangePage)
        .catch(popupMessage),
    );
  };

  const onSubmit = async (amount?: number): Promise<void> => {
    const { tenderExchangeDetails } = store.getState().tenderExchange;
    validateInput(amount);
    const tenderProps = buildTenderProps(tenderExchangeDetails, amount);

    if (!tenderProps) {
      throw new AbortedError(`InvalidTender`);
    }

    await withSpinner(() =>
      addExchangeTender(tenderProps)
        .then(async () => {
          const amountDue = store.getState().itemList.amountDue;
          if (tenderProps.usage === PaymentUsage.ExchangePay) {
            if (Math.abs(amountDue) !== amount) {
              await handlePartialAmountAuthorized(Math.abs(amountDue));
            }
            if (tenderExchange.refundTenders.length === 1) {
              store.dispatch(
                TenderExchangeActions.updateTenderExchangeDetails({
                  exchangeAmount: Math.abs(amountDue),
                  isRefundScreenVisible: true,
                  refundTender: tenderExchange.refundTenders[0],
                  isSingleTenderExchange: true,
                }),
              );
              return onSubmit();
            } else {
              store.dispatch(
                TenderExchangeActions.updateTenderExchangeDetails({
                  exchangeAmount: Math.abs(amountDue),
                  isRefundScreenVisible: true,
                }),
              );
            }
          }
        })
        .catch(async err => {
          // return to non sale if failed in TE pay, and stay in TE if TE refund
          if (tenderProps.usage === PaymentUsage.ExchangePay) {
            checkError(err, tenderExchange);
            await onCancel();
          } else {
            // PO asked to return to non sale if only one refund option when on error
            if (tenderExchange.refundTenders.length === 1) {
              await onCancel();
            }
          }
          throw err;
        }),
    );
  };

  const onVoucherPayment = async (barcode: string): Promise<void> => {
    return withSpinner(() =>
      voucherPayment(barcode, PaymentUsage.ExchangePay)
        .then(amount => {
          popEmeraldModal(ALPHA_NUMERIC_VOUCHER_MODAL);
          const paymentTender = tenderExchange.purchaseTenders.find(
            tenderOption => tenderOption.type === TenderType.Voucher,
          );
          if (tenderExchange.refundTenders.length === 1) {
            store.dispatch(
              TenderExchangeActions.updateTenderExchangeDetails({
                paymentTender,
                exchangeAmount: amount,
                isRefundScreenVisible: true,
                refundTender: tenderExchange.refundTenders[0],
                isSingleTenderExchange: true,
              }),
            );
            return onSubmit();
          } else {
            store.dispatch(
              TenderExchangeActions.updateTenderExchangeDetails({
                paymentTender,
                exchangeAmount: amount,
                isRefundScreenVisible: true,
              }),
            );
          }
        })
        .catch(err => {
          checkError(err, tenderExchange);
          throw err;
        }),
    );
  };

  const props: ITenderExchangeViewProps = {
    onSubmit,
    onVoucherSubmit: onVoucherPayment, // Todo we may also need a voucher refund function
    onCancel,
    tenderExchange,
  };

  if (tenderExchange.purchaseTenders.length === 1) {
    const purchaseTender = tenderExchange.purchaseTenders[0];
    store.dispatch(
      TenderExchangeActions.updateTenderExchangeDetails({ isSingleTenderExchange: true }),
    );
    if (purchaseTender.type === TenderType.Voucher) {
      showSearchTenderVoucherModal({
        onBack: () =>
          store.dispatch(
            TenderExchangeActions.updateTenderExchangeDetails({ paymentTender: undefined }),
          ),
        onVoucherPayment,
      });
    }
    store.dispatch(
      TenderExchangeActions.updateTenderExchangeDetails({ paymentTender: purchaseTender }),
    );
  }

  showTenderExchangePage(props);
};

const buildTenderProps = (
  tenderExchangeDetails: ITenderExchangeDetails,
  amount?: number,
): AddTenderProps | undefined => {
  const { paymentTender, refundTender, exchangeAmount } = tenderExchangeDetails;
  if (paymentTender && !refundTender && amount) {
    return {
      tender: paymentTender,
      amount,
      amountDue: amount,
      lineItemEntryMethod: EntryMethod.Manual,
      usage: PaymentUsage.ExchangePay,
    };
  } else if (refundTender && exchangeAmount) {
    return {
      tender: refundTender,
      amount: Math.abs(exchangeAmount),
      amountDue: Math.abs(exchangeAmount) * -1,
      lineItemEntryMethod: EntryMethod.Manual,
      usage: PaymentUsage.ExchangeReceive,
    };
  }
};

const addExchangeTender = async (tenderProps: AddTenderProps): Promise<void> => {
  if (tenderProps?.tender.isProcessedByEps) {
    const hasPinPad = store.getState().pinPad.isPinpadConfigured;
    if (!hasPinPad) {
      throw pinPadDisconnectModal;
    }
    if (!inPaymentSession()) {
      await beginPaymentSession();
    }
    return showTenderingAndGetCardDetails(tenderProps)
      .then(() => addEFTTender(tenderProps))
      .finally(closeEftTenderingView);
  } else {
    return addTender(tenderProps);
  }
};

// SE does not check zero amount, have to do it in UI
const validateInput = (amount?: number) => {
  if (amount === 0) {
    throw zeroAmountError;
  }
};

const checkError = (err: EmeraldError, tenderExchange: ITenderExchange) => {
  switch (err.code) {
    case TenderExchangeAmountIsOverOrUnderLimitsException:
      throw overRangeError(tenderExchange.minValue, tenderExchange.maxValue);
    case TenderExchangeAmountNotAccordingMultiplesException:
      throw multipleOfError(tenderExchange.multiplesOf || 0);
    default:
      return;
  }
};

const overRangeError = (min?: number, max?: number) => {
  const minAmount = `a minimum of ${convertCurrency(min || 0)}`;
  const maxAmount = max ? `and maximum of ${convertCurrency(max)}` : undefined;

  return new EmeraldErrorModal({
    type: EmeraldDialogTypes.Error,
    message: 'TenderExchangeInvalidAmount',
    subMessage: 'TenderExchangeInvalidAmount_SubMessage',
    subMessageArgs: { arg: minAmount, arg2: maxAmount },
  });
};

const multipleOfError = (multipleOf: number) => {
  return new EmeraldErrorModal({
    type: EmeraldDialogTypes.Error,
    message: 'TenderExchangeInvalidAmount',
    subMessage: 'TenderExchangeInvalidAmount_SubMessage',
    subMessageArgs: { arg: `multiple of ${multipleOf.toString()}` },
  });
};

const zeroAmountError = new EmeraldErrorModal({
  type: EmeraldDialogTypes.Error,
  message: 'TenderExchangeInvalidAmount',
  subMessage: 'TenderRefundAmountIsZeroError',
});

const handlePartialAmountAuthorized = (partialAmount: number): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const partialAmountModal = new EmeraldErrorModal({
      type: EmeraldDialogTypes.Info,
      message: 'ParitalAmountAuth_Title',
      subMessage: 'ParitalAmountAuth_SubMessage',
      subMessageArgs: { arg: convertCurrency(partialAmount) },
      onAccept: resolve,
      onCancel: () => reject(new AbortedError()),
      closeButtonLabel: 'AuthModalCancel',
      acceptButtonLabel: 'Ok',
      separateButton: true,
    });
    popupMessage(partialAmountModal);
  });

export const tenderExchangeSwipeAhead = async (): Promise<EpsData | undefined> => {
  if (!hasCardData()) {
    if (!inPaymentSession()) {
      await beginPaymentSession();
    }
    return enableCardEntry()
      .then(() => waitForCardDetails(1, 1))
      .then(epsData => {
        // Todo: Do we need check this again? We may need find a way which does not trigger swipAhead when select EFT tender
        const updatedTenderClicked = store.getState().tenderExchange.tenderExchangeDetails
          .isTenderClicked;
        if (!updatedTenderClicked) {
          return epsData;
        }
      })
      .catch(err => {
        if (err.statusCode === PmiStatusCodes.Cancelled) {
          // TODO- revisit this when we can handle different cancellation from cashier/customer
          return undefined;
        }
      });
  }
};
